import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileTextIcon, Edit, MoreVertical, Trash, Copy, Download, ListPlus } from 'lucide-react';
import CompactMediaPlayer from './CompactMediaPlayer';
import ClickableUser from './ClickableUser';
import { ForkAttribution, ForkCount } from './ForkAttribution';
import SheetUsage from './SheetUsage';

/**
 * Reusable Sheet Card Component
 * Used across SheetsPage and ProfilePage for consistent sheet display
 * 
 * @param {Object} sheet - The sheet object to display
 * @param {Function} onRename - Callback when rename is clicked
 * @param {Function} onDelete - Callback when delete is clicked
 * @param {Function} onFork - Callback when fork is clicked
 * @param {Function} onExport - Callback when export is clicked
 * @param {Function} onAddToSetlist - Callback when add to setlist is clicked
 * @param {boolean} canManage - Whether the user can manage this sheet (owner rights)
 * @param {string} context - Context where card is used ('sheets', 'profile')
 */
const SheetCard = ({ 
  sheet, 
  onRename,
  onDelete,
  onFork,
  onExport,
  onAddToSetlist,
  canManage = false,
  context = 'sheets'
}) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleCardClick = () => {
    navigate(`/sheet/${sheet._id || sheet.id}?perform=true`);
  };

  return (
    <div 
      className="border border-border rounded-md p-4 bg-card hover:bg-card/80 transition-colors cursor-pointer relative"
      onClick={handleCardClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center overflow-hidden">
          <FileTextIcon className="h-5 w-5 mr-2 flex-shrink-0 text-muted-foreground" />
          <h3 className="text-lg font-medium truncate">{sheet.title || 'Untitled'}</h3>
        </div>
        
        {/* Always show dropdown menu for consistency */}
        <div 
          className="relative" 
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowDropdown(!showDropdown);
            }}
            className="p-1 rounded-md hover:bg-background flex-shrink-0"
          >
            <MoreVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-border">
              {/* Standard actions available to everyone */}
              <button 
                className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(false);
                  navigate(`/sheet/${sheet._id || sheet.id}`);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                View/Edit
              </button>

              {onFork && (
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    onFork(sheet);
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Fork
                </button>
              )}

              {onExport && (
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    onExport(sheet);
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </button>
              )}

              {onAddToSetlist && (
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    onAddToSetlist(sheet);
                  }}
                >
                  <ListPlus className="h-4 w-4 mr-2" />
                  Add to Setlist
                </button>
              )}

              {/* Owner-only actions */}
              {canManage && onRename && (
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    onRename(sheet);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Rename
                </button>
              )}

              {canManage && onDelete && (
                <>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button 
                    className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      onDelete(sheet);
                    }}
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Artist */}
      <div className="text-sm text-muted-foreground truncate">{sheet.artist || 'Unknown Artist'}</div>
      
      {/* Fork attribution if this is a forked sheet */}
      {sheet.forkedFrom && <ForkAttribution sheet={sheet} />}
      {sheet.fork_count > 0 && <ForkCount count={sheet.fork_count} />}
      
      {/* Status indicator, BPM, and Media Player */}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            sheet.status === 'Ready' 
              ? 'bg-green-100 text-green-800 border border-green-300' 
              : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
          }`}>
            {sheet.status === 'Ready' ? '✓ Ready' : '⚠ WIP'}
          </span>
          {sheet.bpm && (
            <span className="text-xs text-muted-foreground">{sheet.bpm} BPM</span>
          )}
        </div>
        {sheet.mediaUrl && (
          <CompactMediaPlayer 
            sheetId={sheet._id || sheet.id}
            mediaUrl={sheet.mediaUrl}
            size="small"
          />
        )}
      </div>
      
      {/* Owner information */}
      {sheet.owner && (
        <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
          Owner:
          <ClickableUser 
            userId={sheet.owner._id || sheet.owner} 
            username={sheet.owner.username || sheet.owner} 
            className="text-xs"
            showIcon={false}
          />
        </div>
      )}
      
      {/* Sheet Usage - which setlists contain this sheet */}
      <SheetUsage sheetId={sheet._id || sheet.id} />
    </div>
  );
};

export default SheetCard;